import { produce, Draft } from 'immer'
import { IAction, TActionTypes } from '@Root/types'
import map from 'lodash/map'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'

import {
  IAccountState,
  AccountActionTypes,
  TAccountActionTypes,
  ISetCurrentAccountPayload,
  IRenameAccountSuccessPayload,
  IChangeAvatarSuccessPayload,
  IChangeTranslationProviderCredentialsSuccessPayload
} from './types'

import {
  TUserActionTypes,
  UserActionTypes,
  IGetUserInfoSuccessResponseBody
} from '@SiteModules/User/types'

import {
  TSubscriptionsActionTypes,
  SubscriptionsActionTypes,
  ISubscription,
  IUpdateSubscriptionSuccessResponseBody,
  ICancelSubscriptionSuccessResponseBody,
} from '@SiteModules/Subscription/types'

import {
  TWorkspaceActionTypes,
  WorkspaceActionTypes,
  ISetCurrentWorkspacePayload,
} from '@SiteModules/Workspace/types'

export const initialState: IAccountState = {
  accounts: [],
  currentAccountId: -1,
  saving: false,
  hasRenamedAccountSuccessfully: false,
  uploadingAvatar: false,
  isUpdatingSubscription: false,
}

type TAction = TActionTypes | TAccountActionTypes | TUserActionTypes | TSubscriptionsActionTypes | TWorkspaceActionTypes

export const accountsReducer = (
  state: IAccountState = initialState,
  action: TAction
): IAccountState => {
  return produce(state, draft => {
    switch (action.type) {
      case UserActionTypes.GET_INFO_SUCCESS:
        loadAccountInfoSuccess(draft, action)
        break

      case AccountActionTypes.RESET_CURRENT_ACCOUNT:
        resetCurrentAccount(draft)
        break

      case AccountActionTypes.SET_CURRENT_ACCOUNT:
        setCurrentAccount(draft, action)
        break

      case AccountActionTypes.RENAME_ACCOUNT:
        renameAccount(draft)
        break

      case AccountActionTypes.RENAME_ACCOUNT_SUCCESS:
        renameAccountSuccess(draft, action)
        break

      case AccountActionTypes.RENAME_ACCOUNT_FAILURE:
        renameAccountFailure(draft)
        break

      case AccountActionTypes.SET_UPLOADING_AVATAR:
        setUploadingAvatar(draft)
        break

      case AccountActionTypes.CHANGE_AVATAR_SUCCESS:
        changeAvatarSuccess(draft, action)
        break

      case AccountActionTypes.CHANGE_AVATAR_FAILURE:
        changeAvatarFailure(draft)
        break

      case AccountActionTypes.CHANGE_TRANSLATION_PROVIDER_CREDENTIALS_SUCCESS:
        changeTranslationProviderCredentialsSuccess(draft, action)
        break

      case SubscriptionsActionTypes.UPDATE_SUBSCRIPTION:
        startUpdatingSubscription(draft)
        break

      case SubscriptionsActionTypes.UPDATE_SUBSCRIPTION_SUCCESS:
        finishUpdatingSubscription(draft)
        updateOrCancelSubscriptionSuccess(draft, action)
        break

      case SubscriptionsActionTypes.UPDATE_SUBSCRIPTION_FAILURE:
        finishUpdatingSubscription(draft)
        break

      case SubscriptionsActionTypes.CANCEL_SUBSCRIPTION_SUCCESS:
        updateOrCancelSubscriptionSuccess(draft, action)
        break

      case WorkspaceActionTypes.SET_CURRENT_WORKSPACE:
        setLastUsedWorkspace(draft, action)
        break

      default:
        break
    }
  })
}

function isGetUserInfoSuccessAction(arg: any): arg is IAction<IGetUserInfoSuccessResponseBody> {
  return arg.type === UserActionTypes.GET_INFO_SUCCESS
}

const loadAccountInfoSuccess = (draft: Draft<IAccountState>, action: TAction) => {
  if (!isGetUserInfoSuccessAction(action)) { return }

  const loadedAccounts = map(action.payload.accounts.data, account => {
    const accountSubscriptionId = account.relationships.subscription.data?.id
    let accountSubscription: ISubscription | undefined = undefined

    if (accountSubscriptionId && action.payload.accounts.included.length > 0) {
      const accountSubscriptionData = find(action.payload.accounts.included, includedAccountSubscriptionData => includedAccountSubscriptionData.id === accountSubscriptionId)
      if (accountSubscriptionData) {
        accountSubscription = { id: parseInt(accountSubscriptionData.id, 10), attributes: accountSubscriptionData.attributes }
      }
    }

    return ({ id: parseInt(account.id, 10), subscription: accountSubscription, attributes: account.attributes })
  })

  draft.accounts = loadedAccounts
}

const resetCurrentAccount = (draft: Draft<IAccountState>) => {
  const accountWithCurrentSubscription = find(draft.accounts, account => !!account.subscription?.attributes.isCurrent)
  if (accountWithCurrentSubscription) {
    draft.currentAccountId = accountWithCurrentSubscription.id
  } else if (draft.accounts.length > 0) {
    draft.currentAccountId = draft.accounts[0].id
  } else {
    draft.currentAccountId = initialState.currentAccountId
  }
}

function isUpdateOrCancelSubscriptionSuccessAction(arg: any): arg is IAction<IUpdateSubscriptionSuccessResponseBody | ICancelSubscriptionSuccessResponseBody> {
  return (arg.type === SubscriptionsActionTypes.UPDATE_SUBSCRIPTION_SUCCESS || arg.type === SubscriptionsActionTypes.CANCEL_SUBSCRIPTION_SUCCESS)
}

const updateOrCancelSubscriptionSuccess = (draft: Draft<IAccountState>, action: TAction) => {
  if (!isUpdateOrCancelSubscriptionSuccessAction(action)) { return }

  const accounts = draft.accounts
  const currentAccountIndex = findIndex(accounts, account => account.id === draft.currentAccountId)
  if (currentAccountIndex < 0) { return }

  const currentAccountWithUpdatedSubscription = {
    ...accounts[currentAccountIndex],
    subscription: {
      id: parseInt(action.payload.data.id, 10),
      attributes: action.payload.data.attributes,
    }
  }

  accounts.splice(currentAccountIndex, 1, currentAccountWithUpdatedSubscription)

  draft.accounts = accounts
}

function isSetCurrentAccountAction(arg: any): arg is IAction<ISetCurrentAccountPayload> {
  return arg.type === AccountActionTypes.SET_CURRENT_ACCOUNT
}

const setCurrentAccount = (draft: Draft<IAccountState>, action: TAction) => {
  if (!isSetCurrentAccountAction(action)) { return }

  draft.currentAccountId = action.payload.accountId
}

function isSetCurrentWorkspaceAction(arg: any): arg is IAction<ISetCurrentWorkspacePayload> {
  return arg.type === WorkspaceActionTypes.SET_CURRENT_WORKSPACE
}

const setLastUsedWorkspace = (draft: Draft<IAccountState>, action: TAction) => {
  if (!isSetCurrentWorkspaceAction(action)) { return }

  const accounts = draft.accounts
  const currentAccountIndex = findIndex(accounts, account => account.id === draft.currentAccountId)
  const currentAccountWithUpdatedLastUsedworkspaceId = { ...accounts[currentAccountIndex], lastUsedWorkspaceId: action.payload.workspaceId }

  accounts.splice(currentAccountIndex, 1, currentAccountWithUpdatedLastUsedworkspaceId)

  draft.accounts = accounts
}

const renameAccount = (draft: Draft<IAccountState>) => {
  draft.saving = true
  draft.hasRenamedAccountSuccessfully = false
}

function isRenameAccountSuccessAction(arg: any): arg is IAction<IRenameAccountSuccessPayload> {
  return arg.type === AccountActionTypes.RENAME_ACCOUNT_SUCCESS
}

const renameAccountSuccess = (draft: Draft<IAccountState>, action: TAction) => {
  if (!isRenameAccountSuccessAction(action)) { return }

  const accounts = draft.accounts
  const accountToRenameIndex = findIndex(accounts, account => account.id === action.payload.accountId)
  draft.accounts[accountToRenameIndex].attributes.name = action.payload.name
  draft.saving = false
  draft.hasRenamedAccountSuccessfully = true
}

const renameAccountFailure = (draft: Draft<IAccountState>) => {
  draft.saving = false
  draft.hasRenamedAccountSuccessfully = false
}

const setUploadingAvatar = (draft: Draft<IAccountState>) => {
  draft.uploadingAvatar = true
}

function isChangeAvatarSuccessAction(arg: any): arg is IAction<IChangeAvatarSuccessPayload> {
  return arg.type === AccountActionTypes.CHANGE_AVATAR_SUCCESS
}

const changeAvatarSuccess = (draft: Draft<IAccountState>, action: TAction) => {
  if (!isChangeAvatarSuccessAction(action)) { return }

  const accounts = draft.accounts
  const accountToChangeIndex = findIndex(accounts, account => account.id === action.payload.accountId)
  draft.accounts[accountToChangeIndex].attributes.avatarUrl = action.payload.url
  draft.uploadingAvatar = false
}

const changeAvatarFailure = (draft: Draft<IAccountState>) => {
  draft.uploadingAvatar = false
}

function isChangeTranslationProviderCredentialsSuccessAction(arg: any): arg is IAction<IChangeTranslationProviderCredentialsSuccessPayload> {
  return arg.type === AccountActionTypes.CHANGE_TRANSLATION_PROVIDER_CREDENTIALS_SUCCESS
}

const changeTranslationProviderCredentialsSuccess = (draft: Draft<IAccountState>, action: TAction) => {
  if (!isChangeTranslationProviderCredentialsSuccessAction(action)) { return }

  const accounts = draft.accounts
  const accountToChangeIndex = findIndex(accounts, account => account.id === action.payload.accountId)
  if (action.payload.translationProvider) {
    draft.accounts[accountToChangeIndex].attributes.translationProvider = action.payload.translationProvider
  }
  draft.accounts[accountToChangeIndex].attributes.translationProviderId = action.payload.translationProviderId
  draft.accounts[accountToChangeIndex].attributes.translationProviderSecret = action.payload.translationProviderSecret
}

const startUpdatingSubscription = (draft: Draft<IAccountState>) => {
  draft.isUpdatingSubscription = true
}

const finishUpdatingSubscription = (draft: Draft<IAccountState>) => {
  draft.isUpdatingSubscription = false
}
